import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  SETTINGS_OS_FLOOR,
  SU_PLATFORM_META,
  SoftwareUpdateWizardState,
  SuAuto,
  UpdateBehaviorState,
} from "../types";

interface Props {
  state: SoftwareUpdateWizardState;
  onChange: (patch: Partial<UpdateBehaviorState>) => void;
}

const AUTO_OPTIONS: Array<{ value: SuAuto; label: string }> = [
  { value: "Allowed", label: "Allowed (Apple default)" },
  { value: "AlwaysOn", label: "Always On" },
  { value: "AlwaysOff", label: "Always Off" },
];

function AutoSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: SuAuto;
  onChange: (v: SuAuto) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as SuAuto)}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {AUTO_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (n: number) => void;
  hint?: string;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        max={90}
        value={value || ""}
        placeholder="—"
        onChange={(e) => {
          const raw = e.target.value;
          if (!raw) return onChange(0);
          const n = parseInt(raw, 10);
          if (Number.isNaN(n)) return;
          onChange(Math.max(0, Math.min(90, n)));
        }}
      />
      <p className="text-xs text-muted-foreground">{hint ?? "1–90 days. Leave blank to inherit Apple's default."}</p>
    </div>
  );
}

export function UpdateBehaviorForm({ state, onChange }: Props) {
  const b = state.behavior;
  const platform = state.platform;
  const isMac = platform === "macos";
  const isIosFamily = platform === "ios" || platform === "ipados";
  const supportsRsr = platform === "ios" || platform === "macos";
  const floor = SETTINGS_OS_FLOOR[platform];
  const offerActive = b.betaEnrollment !== "AlwaysOff" && !b.betaRequireProgram;
  const requireActive = b.betaEnrollment === "AlwaysOn" && !!b.betaRequireProgram;

  return (
    <div className="space-y-6">
      {floor && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
          <AlertTriangle className="h-4 w-4 text-warning mt-0.5" aria-hidden="true" />
          <div>
            <div className="font-medium text-foreground">Supported OS floor</div>
            <div className="text-muted-foreground">
              Update behavior requires {SU_PLATFORM_META[platform].label} {floor}+. Below the floor, the
              declaration is ignored.
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Deferrals</CardTitle>
          <CardDescription>How long the device hides updates from the user. Integers 1–90.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {isMac && (
            <>
              <NumberField id="defer-major" label="Defer major updates (days)" value={b.deferMajorDays} onChange={(n) => onChange({ deferMajorDays: n })} />
              <NumberField id="defer-minor" label="Defer minor updates (days)" value={b.deferMinorDays} onChange={(n) => onChange({ deferMinorDays: n })} />
              <NumberField id="defer-system" label="Defer system / non-OS updates (days)" value={b.deferSystemDays} onChange={(n) => onChange({ deferSystemDays: n })} />
            </>
          )}
          {!isMac && (
            <NumberField id="defer-combined" label="Defer updates (days)" value={b.deferCombinedDays} onChange={(n) => onChange({ deferCombinedDays: n })} />
          )}
        </CardContent>
      </Card>

      {isIosFamily && (
        <Card>
          <CardHeader>
            <CardTitle>Recommended cadence</CardTitle>
            <CardDescription>Which versions iOS / iPadOS surfaces in Settings.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:max-w-xs">
            <Label htmlFor="cadence">Which versions to show</Label>
            <Select
              value={b.recommendedCadence || "default"}
              onValueChange={(v) => onChange({ recommendedCadence: v === "default" ? "" : (v as typeof b.recommendedCadence) })}
            >
              <SelectTrigger id="cadence">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Apple default</SelectItem>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="Oldest">Oldest</SelectItem>
                <SelectItem value="Newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Automatic actions</CardTitle>
          <CardDescription>What the OS may do without user interaction.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="auto-download">Automatic download</Label>
            <AutoSelect id="auto-download" value={b.autoDownload} onChange={(v) => onChange({ autoDownload: v })} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auto-install">Automatic OS install</Label>
            <AutoSelect id="auto-install" value={b.autoInstallOs} onChange={(v) => onChange({ autoInstallOs: v })} />
          </div>
          {isMac && (
            <div className="grid gap-2">
              <Label htmlFor="auto-security">Automatic security install</Label>
              <AutoSelect id="auto-security" value={b.autoInstallSecurity} onChange={(v) => onChange({ autoInstallSecurity: v })} />
              <p className="text-xs text-muted-foreground">Maps to singular <code>InstallSecurityUpdate</code>.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {supportsRsr && (
        <Card>
          <CardHeader>
            <CardTitle>Background Security Improvements</CardTitle>
            <CardDescription>Rapid Security Response (RSR) on {SU_PLATFORM_META[platform].label}.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Offer Background Security Improvements</span>
              <Switch checked={b.rsrEnable} onCheckedChange={(v) => onChange({ rsrEnable: v })} />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Allow rollback of BSI</span>
              <Switch checked={b.rsrEnableRollback} onCheckedChange={(v) => onChange({ rsrEnableRollback: v })} />
            </label>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>User experience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isMac && (
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Allow standard users to update</span>
              <Switch
                checked={b.allowStandardUserOsUpdates}
                onCheckedChange={(v) => onChange({ allowStandardUserOsUpdates: v })}
              />
            </label>
          )}
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Show all update notifications</span>
            <Switch checked={b.notifications} onCheckedChange={(v) => onChange({ notifications: v })} />
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Beta program (AppleSeed)</CardTitle>
          <CardDescription>
            Offer and Require are mutually exclusive. Require is only valid when enrollment is <strong>Always On</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2 sm:max-w-xs">
            <Label htmlFor="beta-enrollment">Enrollment</Label>
            <Select value={b.betaEnrollment} onValueChange={(v) => onChange({ betaEnrollment: v as SuAuto })}>
              <SelectTrigger id="beta-enrollment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTO_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {offerActive && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Offer programs</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onChange({
                      betaOfferPrograms: [...b.betaOfferPrograms, { description: "", token: "" }],
                      betaRequireProgram: null,
                    })
                  }
                >
                  <Plus className="h-3 w-3 mr-1" /> Add program
                </Button>
              </div>
              {b.betaOfferPrograms.length === 0 ? (
                <p className="text-xs text-muted-foreground">No optional beta programs offered to users.</p>
              ) : (
                b.betaOfferPrograms.map((p, i) => (
                  <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
                    <Input
                      placeholder="Description"
                      value={p.description}
                      onChange={(e) => {
                        const next = [...b.betaOfferPrograms];
                        next[i] = { ...next[i], description: e.target.value };
                        onChange({ betaOfferPrograms: next });
                      }}
                    />
                    <Input
                      placeholder="Token"
                      value={p.token}
                      onChange={(e) => {
                        const next = [...b.betaOfferPrograms];
                        next[i] = { ...next[i], token: e.target.value };
                        onChange({ betaOfferPrograms: next });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove program"
                      onClick={() =>
                        onChange({ betaOfferPrograms: b.betaOfferPrograms.filter((_, j) => j !== i) })
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}

          {b.betaEnrollment === "AlwaysOn" && (
            <div className="space-y-2">
              <Label>Required program (mutually exclusive with Offer)</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input
                  placeholder="Description"
                  value={b.betaRequireProgram?.description ?? ""}
                  onChange={(e) =>
                    onChange({
                      betaRequireProgram: { description: e.target.value, token: b.betaRequireProgram?.token ?? "" },
                      betaOfferPrograms: [],
                    })
                  }
                />
                <Input
                  placeholder="Token"
                  value={b.betaRequireProgram?.token ?? ""}
                  onChange={(e) =>
                    onChange({
                      betaRequireProgram: { description: b.betaRequireProgram?.description ?? "", token: e.target.value },
                      betaOfferPrograms: [],
                    })
                  }
                />
              </div>
              {requireActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange({ betaRequireProgram: null })}
                >
                  Clear required program
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
